import { useEffect } from "react";
import type { ConversationStatus } from "@/api/agent-conversation";
import { ExecutionTerminal } from "@/components/agents/conversation/ExecutionTerminal";
import { watchingOf, type TerminalSource } from "@/components/agents/conversation/terminalSource";
import type { ConversationAdapter } from "@/components/agents/conversation/types";
import { useCurrentUser } from "@/hooks/useAuth";
import { useAgentConversation } from "@/hooks/useAgentConversation";

/**
 * Conversa com um agente de configuração (spec 2026-10-09, §10): liga o log do run ao
 * terminal único (`ExecutionTerminal`) por `useAgentConversation`. O nó de origem
 * chega pelo `adapter` (rotas da conversa e o run dos prints), que o chamador monta
 * com `buildNodeAdapter` (`api/stage-conversations.ts`); a tela não conhece o nó.
 */
export function AgentConversation({
  adapter,
  title = null,
  onStatusChange,
}: {
  adapter: ConversationAdapter;
  onStatusChange?: (status: ConversationStatus | null) => void;
  /** "código · nome" da configuração, no cabeçalho do console. */
  title?: string | null;
}) {
  return (
    <Conversation key={JSON.stringify([adapter.projectId, adapter.runId])} adapter={adapter} title={title} onStatusChange={onStatusChange} />
  );
}

// Cada adaptador é uma fonte: Parar/Encerrar pendentes de um não alcançam o seguinte,
// mesmo com o mesmo projeto e run. A chave é texto, então o objeto ganha um id.
const adapterScopes = new WeakMap<ConversationAdapter, string>();
let nextAdapterScope = 0;
function adapterScope(adapter: ConversationAdapter): string {
  let scope = adapterScopes.get(adapter);
  if (scope === undefined) {
    nextAdapterScope += 1;
    scope = `adapter-${nextAdapterScope}`;
    adapterScopes.set(adapter, scope);
  }
  return scope;
}

/** A identidade da conversa reinicia cartões, rascunho e confirmação juntos. */
function Conversation({ adapter, title, onStatusChange }: { adapter: ConversationAdapter; title: string | null; onStatusChange?: (status: ConversationStatus | null) => void }) {
  const conversation = useAgentConversation(adapter);
  const viewer = useCurrentUser();
  useEffect(() => {
    onStatusChange?.(conversation.state.status);
  }, [conversation.state.status, onStatusChange]);
  const source: TerminalSource = {
    ...conversation,
    scopeKey: adapterScope(adapter),
    media: { projectId: adapter.projectId, runId: adapter.runId },
    watching: watchingOf(conversation.state.executor, viewer?.id),
  };
  return <ExecutionTerminal source={source} title={title} />;
}
